import React, { useEffect, useMemo, useState } from "react";
import { Search, Globe, Edit2 } from "lucide-react";
import NotificationAlert from "../../../CustomAlerts/NotificationAlert";
import {
  GetSources,
  CreateSource,
  UpdateSource,
} from "../services/MasterDataService";

const SourceMasterPage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const [selectedSource, setSelectedSource] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [alert, setAlert] = useState(null);

  const fetchSources = async () => {
    try {
      setLoading(true);

      const res = await GetSources();

      if (res?.status === "success") {
        setSources(res.sources || []);
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Failed to load sources.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.message || "Failed to fetch sources. Please try again later.",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  // ---------------- FILTER ----------------
  const filteredSources = useMemo(() => {
    const query = search.toLowerCase();

    return sources.filter(
      (x) => !query || x.source?.toLowerCase().includes(query),
    );
  }, [sources, search]);

  // ---------------- PAGINATION ----------------
  const totalPages = Math.ceil(filteredSources.length / pageSize);

  const paginatedSources = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filteredSources.slice(start, start + pageSize);
  }, [filteredSources, page, pageSize]);

  const startRecord =
    filteredSources.length === 0 ? 0 : (page - 1) * pageSize + 1;

  const endRecord = Math.min(page * pageSize, filteredSources.length);

  const totalSources = filteredSources.length;

  const handleSaveSource = async () => {
    if (!sourceName || !sourceName.trim()) {
      setAlert({
        type: "error",
        message: "Source name is required.",
      });
      return;
    }
    try {
      setActionLoading(true);

      let res;

      if (isEditMode) {
        res = await UpdateSource({
          sourceUID: selectedSource.sourceUID,
          source: sourceName,
        });
      } else {
        res = await CreateSource(sourceName);
      }

      if (res?.status === "success") {
        setAlert({
          type: "success",
          message: res.message,
        });

        setTimeout(() => {
          setShowModal(false);
        }, 1200);

        fetchSources();
      } else {
        setAlert({
          type: "error",
          message: res?.message,
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Something went wrong.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="p-2 space-y-3">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sources</h1>
          <p className="text-slate-500 mt-1">Manage source master data.</p>
        </div>

        {/* TOOLBAR */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative w-[320px]">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  placeholder="Search sources..."
                  className="w-full pl-10 pr-3 h-10 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none"
                />
              </div>

              <span className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-medium text-slate-700">
                  {startRecord}
                </span>
                {" - "}
                <span className="font-medium text-slate-700">{endRecord}</span>
                {" of "}
                <span className="font-medium text-slate-700">
                  {filteredSources.length}
                </span>
              </span>
            </div>

            <button
              onClick={() => {
                setIsEditMode(false);
                setSelectedSource(null);
                setSourceName("");
                setShowModal(true);
              }}
              className="h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium"
            >
              + Add Source
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-semibold text-slate-500">
            <div className="col-span-5">Source</div>
            <div className="col-span-3">Created By</div>
            <div className="col-span-2">Created Date</div>
            <div className="col-span-2 text-center">Actions</div>
          </div>

          <div className="divide-y divide-slate-100">
            {loading ? (
              <div className="py-16 text-center">Loading sources...</div>
            ) : paginatedSources.length === 0 ? (
              <div className="py-16 text-center">
                <Globe size={28} className="mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600">No sources found</p>
              </div>
            ) : (
              paginatedSources.map((item) => (
                <div
                  key={item.sourceUID}
                  className="grid grid-cols-12 px-6 py-4 hover:bg-blue-50/40 transition-all"
                >
                  <div className="col-span-5 flex items-center">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-700">
                        <Globe size={18} />
                      </div>

                      <span className="font-medium text-slate-800">
                        {item.source}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-3 flex items-center text-sm text-slate-600">
                    {item.createdBy}
                  </div>

                  <div className="col-span-2 flex flex-col justify-center">
                    <span className="text-sm text-slate-700">
                      {new Date(item.createdDate).toLocaleDateString("en-GB")}
                    </span>

                    <span className="text-xs text-slate-500">
                      {new Date(item.createdDate).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>

                  <div className="col-span-2 flex items-center justify-center">
                    <button
                      onClick={() => {
                        setIsEditMode(true);
                        setSelectedSource(item);
                        setSourceName(item.source ?? "");
                        setShowModal(true);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all text-sm font-medium"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* PAGINATION (EXACT SAME AS ORGANIZATION) */}
          {!initialLoading && totalPages >= 1 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                    ${
                      page === 1
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                >
                  Prev
                </button>

                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm text-slate-400">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`h-8 min-w-[34px] px-2 rounded-lg text-sm font-semibold transition-all
                            ${
                              page === pageNumber
                                ? "bg-primary text-white shadow-sm"
                                : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                    ${
                      page === totalPages
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL (IDENTICAL STYLE TO ORGANIZATION) */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-white shadow-xl p-6"
          >
            <h2 className="text-xl font-semibold text-slate-800">
              {isEditMode ? "Edit Source" : "Add Source"}
            </h2>

            <div className="mt-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Source Name <span className="text-red-500">*</span>
              </label>

              <textarea
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                maxLength={50}
                rows={3}
                placeholder="Enter source name"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none focus:ring-2 focus:ring-blue-300 outline-none"
              />

              <div
                className={`mt-1 text-xs text-right font-semibold ${
                  sourceName.length > 40
                    ? "!text-orange-500"
                    : "!text-slate-400"
                }`}
              >
                {sourceName.length}/50
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600"
              >
                Cancel
              </button>

              <button
                disabled={actionLoading || !sourceName.trim()}
                onClick={handleSaveSource}
                className={`px-4 py-2 rounded-xl text-white 
    ${sourceName.trim() ? "bg-sky-600 hover:bg-sky-700" : "bg-slate-300 cursor-not-allowed"}
  `}
              >
                {actionLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ALERT */}
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default SourceMasterPage;
